// src/theme.js
import { createTheme } from '@mui/material/styles';

const ACCENT_GOLD = '#f59e0b';
const INK_DARK = '#1c1400';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: ACCENT_GOLD,
      dark: '#d97706',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: INK_DARK,
      dark: '#0f0d00',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#fefce8',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#212121',
      secondary: '#616161',
    },
    divider: '#E0E0E0',
  },

  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h3: { fontWeight: 700, color: '#212121' },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 500 },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },

  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'rgba(254, 252, 232, 0.9)',
          color: INK_DARK,
          boxShadow: 'none',
          borderBottom: '1px solid rgba(240, 208, 96, 0.25)',
          backdropFilter: 'blur(12px)',
        },
      },
    },

    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: '#f59e0b',
          border: '1px solid #fde047',
          color: INK_DARK,
          fontWeight: 700,
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
          },
        },
        containedPrimary: {
          background: INK_DARK,
          color: '#fefce8',
          '&:hover': {
            background: '#2a2000',
            boxShadow: '0 6px 12px rgba(0, 0, 0, 0.2)',
          },
        },
        containedSecondary: {
          background: ACCENT_GOLD,
          color: INK_DARK,
          '&:hover': {
            background: '#d97706',
            boxShadow: '0 6px 12px rgba(0, 0, 0, 0.2)',
          },
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          backdropFilter: 'blur(12px)',
          backgroundColor: 'rgba(255, 255, 255, 0.4)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          borderRadius: 16,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-6px)', // ✅ fixed error
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },

    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          border: '1px solid #E0E0E0',
          boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: '0 0 16px 0' },
        },
      },
    },

    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          backgroundColor: '#F8F8F8',
          color: '#212121',
          borderBottom: '1px solid #E0E0E0',
          '& .MuiTypography-root': { fontWeight: 600 },
          '& .MuiAccordionSummary-expandIconWrapper .MuiSvgIcon-root': {
            color: ACCENT_GOLD,
          },
        },
      },
    },

    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          padding: '16px 24px',
        },
      },
    },

    MuiCssBaseline: {
      styleOverrides: {
        body: {
          '&::-webkit-scrollbar': { width: 8 },
          '&::-webkit-scrollbar-thumb': {
            background: ACCENT_GOLD,
            borderRadius: 8,
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(253, 224, 71, 0.1)',
          },
          background: '#fefce8',
        },

        footer: {
          textAlign: 'center',
          padding: '4px 8px',
          fontSize: '12px',
          width: '100%',
          marginTop: 'auto',
          background: '#fefce8',
          borderTop: '1px solid #f0d060',
          color: '#6a6000',
          lineHeight: 1.4,
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          // Default MUI styles retained
        },
      },
    },
  },
});

export default theme;
